/**
 * The handful of DOM conveniences the components share.
 *
 * Everything is built from elements and text nodes rather than HTML strings:
 * display names arrive over the network, and nothing that does should ever be
 * parsed as markup.
 */

export type AttributeValue = boolean | number | string | null | undefined;

export type Child = Node | string | false | null | undefined;

export type Attributes = Readonly<Record<string, AttributeValue>>;

/** Sets or clears an attribute: `true` is a bare flag, while `false` and nullish remove it. */
export function setAttribute(element: Element, name: string, value: AttributeValue): void {
    if (value === false || value === null || value === undefined) {
        element.removeAttribute(name);
        return;
    }

    element.setAttribute(name, value === true ? "" : String(value));
}

export function setAttributes(element: Element, attributes: Attributes): void {
    for (const [name, value] of Object.entries(attributes)) {
        setAttribute(element, name, value);
    }
}

/** Drops the placeholders a conditional child leaves behind. */
export function toNodes(children: readonly Child[]): readonly (Node | string)[] {
    return children.filter((child) => child !== false && child !== null && child !== undefined);
}

/**
 * Runs a rebuild of a shadow tree and puts focus back on the control it was
 * on. Controls opt in with a `data-focus-key` that names what they act on -
 * "meld-7", say - so the rebuilt control for the same thing takes the focus
 * its predecessor had, instead of a keyboard player being dropped to the page.
 */
export function keepFocus(root: ShadowRoot, rebuild: () => void): void {
    const active = root.activeElement;
    const key = active instanceof HTMLElement ? active.dataset.focusKey : undefined;

    rebuild();

    if (key !== undefined && root.activeElement !== active) {
        root.querySelector<HTMLElement>(`[data-focus-key="${CSS.escape(key)}"]`)?.focus();
    }
}

/** Builds one element with its attributes and children. */
export function h<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    attributes: Attributes = {},
    ...children: readonly Child[]
): HTMLElementTagNameMap[K] {
    const element = document.createElement(tag);

    setAttributes(element, attributes);
    element.append(...toNodes(children));

    return element;
}
